import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createApp } from "../api/apps.api";
import { createDeployment, listDeployments } from "@/features/deployments/api/deployments.api";
import { deploymentsKeys } from "@/features/deployments/hooks/useDeployments";
import { appsKeys } from "./useApps";
import {
  initialProgress,
  runImport,
  type ImportApi,
  type ImportPlan,
  type ImportProgress,
  type ImportRow,
} from "../lib/codepush-import";

const importApi: ImportApi = { createApp, createDeployment, listDeployments };

export function useImportCodepushApp() {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<ImportProgress>(() => initialProgress([]));
  const [isRunning, setIsRunning] = useState(false);
  const progressRef = useRef(progress);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const publish = useCallback((next: ImportProgress) => {
    progressRef.current = next;
    if (mounted.current) setProgress(next);
  }, []);

  const reset = useCallback(
    (rows: ImportRow[]) => {
      publish(initialProgress(rows));
      setIsRunning(false);
    },
    [publish],
  );

  const clearAppFailure = useCallback(() => {
    const current = progressRef.current;
    if (current.app.phase !== "failed") return;
    publish({ ...current, app: { phase: "pending" } });
  }, [publish]);

  const clearRowFailure = useCallback(
    (id: string) => {
      const current = progressRef.current;
      if (current.rows[id]?.phase !== "failed") return;
      publish({ ...current, rows: { ...current.rows, [id]: { phase: "pending" } } });
    },
    [publish],
  );

  const submit = useCallback(
    async (plan: ImportPlan) => {
      setIsRunning(true);
      try {
        const latest = await runImport(plan, progressRef.current, importApi, publish);
        publish(latest);
        return latest;
      } finally {
        if (mounted.current) setIsRunning(false);
        if (progressRef.current.app.phase === "ready") {
          const { appName } = progressRef.current.app;
          queryClient.invalidateQueries({ queryKey: appsKeys.all });
          queryClient.invalidateQueries({ queryKey: deploymentsKeys.all(appName) });
        }
      }
    },
    [publish, queryClient],
  );

  return { progress, isRunning, submit, reset, clearAppFailure, clearRowFailure };
}
